import { createSelector } from 'reselect'

import { selectAgentsSortedByUsername } from 'selectors/agents/base'
import {
  selectDeviceTypeIsDesktop,
  selectCanMentionAnyAgent,
} from 'selectors/app'
import { selectCurrentUser } from 'ducks/currentUser/selectors'
import {
  selectCurrentMailboxAgents,
  selectNewNoteDraftMailboxAgents,
} from 'selectors/access_list'
import { selectIsLoggingNewConversation } from 'selectors/page'
import { selectCurrentTicket } from 'selectors/tickets'
import { selectCurrentTicketRawActions } from 'selectors/currentChangesets'
import { selectTicketDraft } from 'ducks/drafts'
import { filterAgentsByTerm } from 'util/agents'

export const selectAgentMention = state => state.app.agentMention

export const selectIsMentioningAgent = state => {
  if (!state.app.agentMention) return false
  return state.app.agentMention.length > 0
}

export const selectSuppressMentionSelector = state =>
  !!state.app.suppressMentionSelector

const selectAgentsWithAccess = createSelector(
  selectCurrentUser,
  selectCurrentMailboxAgents,
  (currentUser, agents) => {
    return agents.filter(agent => agent.id !== currentUser.id)
  }
)

// Ticket Note form mentions
//
// Extra layer of logic whereby the `mention_any_agent` preference skips the
// access_list logic entirely.
export const selectMentionableAgents = createSelector(
  selectCanMentionAnyAgent,
  selectAgentsSortedByUsername,
  selectAgentsWithAccess,
  (canMentionAnyone, allAgents, agentsWithAccess) => {
    if (!canMentionAnyone) return agentsWithAccess

    return allAgents
  }
)

export const selectFilteredMentionableAgents = createSelector(
  selectMentionableAgents,
  selectAgentMention,
  (agents, mention) => {
    const word = mention ? mention.word : null
    if (word && word.charAt(0) === '@') {
      const cut = word.substr(1)
      return filterAgentsByTerm(agents, cut)
    }
    return agents
  }
)

const selectNewNoteDraftAgentsWithAccess = createSelector(
  selectCurrentUser,
  selectNewNoteDraftMailboxAgents,
  (currentUser, agents) => {
    return agents.filter(agent => agent.id !== currentUser.id)
  }
)

// Log Conversation (i.e. New Note) mentions
export const selectNewNoteDraftMentionableAgents = createSelector(
  selectCanMentionAnyAgent,
  selectAgentsSortedByUsername,
  selectNewNoteDraftAgentsWithAccess,
  (canMentionAnyone, allAgents, agentsWithAccess) => {
    if (!canMentionAnyone) return agentsWithAccess

    return allAgents
  }
)

const selectNewNoteDraftFilteredMentionableAgents = createSelector(
  selectNewNoteDraftMentionableAgents,
  selectAgentMention,
  (agents, mention) => {
    const word = mention ? mention.word : null
    if (word && word.charAt(0) === '@') {
      const cut = word.substr(1)
      return filterAgentsByTerm(agents, cut)
    }
    return agents
  }
)

// Main entrypoint for both Note and Log Conversation forms.
export const selectTicketOrNoteDraftFilteredMentionableAgents = createSelector(
  selectIsLoggingNewConversation,
  selectNewNoteDraftFilteredMentionableAgents,
  selectFilteredMentionableAgents,
  (onNewNotePage, draftAgents, ticketAgents) => {
    if (!onNewNotePage) return ticketAgents

    return draftAgents
  }
)

export const selectShowMentionSelector = createSelector(
  selectIsMentioningAgent,
  selectSuppressMentionSelector,
  selectFilteredMentionableAgents,
  selectDeviceTypeIsDesktop,
  (isMentioning, isSuppressing, filtered, isDesktop) => {
    if (isDesktop) {
      return isMentioning && !isSuppressing && filtered.length > 0
    }
    return isMentioning && !isSuppressing
  }
)

export const selectSuggestedMentionableAgents = createSelector(
  selectMentionableAgents,
  selectCurrentTicket, // could return null
  selectTicketDraft,
  selectCurrentTicketRawActions,
  (agents, ticket, draft, actions) => {
    const shortList = {}
    // Assignee added in draft
    if (draft && draft.assignee) shortList[draft.assignee.id] = true
    // Assignee
    if (ticket && ticket.assignee) shortList[ticket.assignee.id] = true
    // Author of a comment on this ticket (note or reply)
    const records = actions
    if (records) {
      records.forEach(action => {
        const actor = action.actor
        if (actor.username) {
          shortList[actor.id] = true
        }
      })
    }
    // Agent that assigned ticket to current assignee
    const filtered = agents.filter(agent => shortList[agent.id])
    return filtered.length === 0 ? agents : filtered
  }
)

const selectFirstMentionableAgent = createSelector(
  selectMentionableAgents,
  selectSuggestedMentionableAgents,
  selectFilteredMentionableAgents,
  selectAgentMention,
  (sorted, suggested, filtered, mention) => {
    if (!mention) return null
    let result = null
    if (mention.word === '@') {
      result = suggested[0]
    } else {
      result = filtered[0]
    }
    if (!result) result = sorted[0]
    return result
  }
)

export const selectSelectedMentionSelectorAgentId = createSelector(
  state => state.app.selectedMentionSelectorAgentId,
  selectFirstMentionableAgent,
  (selectedAgentId, firstMentionableAgent) => {
    if (!selectedAgentId && firstMentionableAgent) {
      return firstMentionableAgent.id
    }
    return selectedAgentId
  }
)
